/**
 * Which company a formula name in a cell runs against.
 *
 * A bare name always means the session's active company — the one the company
 * switcher shows. A formula from any other company, the user's own home
 * company included when they have switched into a demo or client company,
 * must carry that company's short name, or the cell silently runs the active
 * company's formula of the same name with the wrong arguments.
 */

export interface FormulaCompany {
  companyId: string;
  shortName: string | null;
  /** Whether this is the session's active company. */
  isActive: boolean;
}

export function needsPrefix(company: FormulaCompany): boolean {
  return !company.isActive;
}

/** The name as a cell writes it: `"NAG:NETCHANGE"`, or `"NETCHANGE"` for the active company. */
export function formulaCallName(name: string, company: FormulaCompany): string {
  const upper = name.toUpperCase();
  return needsPrefix(company) && company.shortName ? `${company.shortName}:${upper}` : upper;
}

/** A company other than the active one without a short name cannot be reached from a cell. */
export function isUnreachable(company: FormulaCompany): boolean {
  return needsPrefix(company) && !company.shortName;
}

/**
 * The session's active company, or the user's home company when the session
 * names none the list contains (a token issued before a company was re-created).
 */
export function activeCompanyId<T extends { companyId: string; type: string }>(companies: T[], sessionCompanyId: string | null | undefined): string | null {
  if (sessionCompanyId && companies.some(c => c.companyId === sessionCompanyId)) return sessionCompanyId;
  return companies.find(c => c.type === 'primary')?.companyId ?? null;
}
